import React, { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { useRouter } from 'next/router';

import { subscribe } from '../../../../api/internal';
import { isUserLoggedIn } from '../../../../utils/analytics';
import { useStore } from '../../../../store';
import { useIntersectionObserver } from '../../../../hooks/useIntersectionObserver';
import SubscribeSection from '../../../../ui/organisms/SubscribeSection';
import { logSentryError } from '../../../../utils/errors';
import { useRecaptcha } from '../../../../utils/recaptcha';

const SubscribeSectionContainer: React.FC = () => {
  const SCROLL_QUERY_PARAM = 'subscribe-now';
  const router = useRouter();

  const { state } = useStore();
  const { execute: executeRecaptcha } = useRecaptcha();
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasSubscribed, setHasSubscribed] = useState<boolean>(false);
  const [hasSentViewEvent, setHasSentViewEvent] = useState<boolean>(false);
  const [hasSubscribeNowQuery, sethasSubscribeNowQuery] = useState<boolean>(false);

  const subscribeSectionRef = useRef<HTMLDivElement | null>(null);
  const [isSubscribeSectionViewVisible] = useIntersectionObserver(subscribeSectionRef, {
    root: null,
    rootMargin: '-64px 0px 0px 0px', // Set top margin to take into account navigation header
    threshold: 0.5,
  });

  let heading: string | undefined;
  let subheading: string | undefined;

  // Capture view event if subscribe scrolls into viewport
  useEffect(() => {
    if (!isSubscribeSectionViewVisible || hasSentViewEvent) {
      return;
    }

    posthog.capture('subscribe_banner_view', {
      event_location: 'home_subscribe_cta',
      is_logged_in: isUserLoggedIn(state.user),
    });

    setHasSentViewEvent(true);
  }, [isSubscribeSectionViewVisible]);

  // Scroll to subscribe component if marketing from marketing query link
  useEffect(() => {
    const hasSubscribeNow = SCROLL_QUERY_PARAM in router.query;
    sethasSubscribeNowQuery(hasSubscribeNow);
  }, [router.query]);

  const handlePosthogCapture = (eventName: string) => {
    const HOME_SUBSCRIBE_EVENT_LOCATION = 'home_subscribe';
    const HOME_SUBSCRIBE_TAGS = ['home_subscribe_cta'];

    posthog.capture(eventName, {
      event_location: HOME_SUBSCRIBE_EVENT_LOCATION,
      is_logged_in: isUserLoggedIn(state.user),
      cta_text: "Don't miss a drop!",
      tags: HOME_SUBSCRIBE_TAGS,
    });
  };

  const onSubmit = async (emailInput: string): Promise<void> => {
    const homeSubscribeTag = 'home_subscribe';

    try {
      handlePosthogCapture('subscribe_cta_click');
      const captchaString = await executeRecaptcha();
      await subscribe(emailInput, captchaString, homeSubscribeTag);
      handlePosthogCapture('user_subscribed');
      setHasSubscribed(true);
    } catch (error) {
      logSentryError(error, 'subscription error', 'Failed to subscribe customer in Shopify');
      setHasError(true);
      setHasSubscribed(false);
    }
  };

  return (
    <div ref={subscribeSectionRef}>
      <SubscribeSection
        heading={heading}
        subheading={subheading}
        onSubmit={onSubmit}
        hasError={hasError}
        hasSubscribed={hasSubscribed}
        scrollToSection={hasSubscribeNowQuery}
      />
    </div>
  );
};

export default SubscribeSectionContainer;
